import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REQUEST_ITEM_PHOTOS_MAX } from '@magimp/shared';
import { requestUploadUrl } from '../../api/requests/index.js';
import {
  compressImage,
  imageSourceFormat,
  isPhotoCompressError,
  isUnsupportedSvgImage,
} from './photoCompress.js';
import { useRequestsStore } from './store.js';

export interface DraftPhotoUploadApi {
  itemUploading: Record<string, boolean>;
  itemUploadError: Record<string, string>;
  boxItemUploading: Record<string, boolean>;
  boxItemUploadError: Record<string, string>;
  onUploadPhoto: (itemId: string, file: File) => Promise<void>;
  // WHY (#1451): SIMPLE multi-photo add. Same compress→presign→PUT flow
  //   as `onUploadPhoto` but appends the resolved key (cap-guarded)
  //   instead of replacing the single slot. Resolves to the appended
  //   `{ objectKey }` on success, or `null` on cap / in-flight / failure
  //   so the caller can bind the right blob preview (or revoke it).
  onAddItemPhoto: (itemId: string, file: File) => Promise<{ objectKey: string } | null>;
  onUploadBoxItemPhoto: (boxClientId: string, boxItemClientId: string, file: File) => Promise<void>;
}

export function useDraftPhotoUpload(): DraftPhotoUploadApi {
  const { t } = useTranslation(['requests']);
  const setItemPhoto = useRequestsStore((s) => s.setDraftItemPhoto);
  const addItemPhoto = useRequestsStore((s) => s.addDraftItemPhoto);
  const setBoxItemPhoto = useRequestsStore((s) => s.setDraftBoxItemPhoto);
  const trackUnsavedPhoto = useRequestsStore((s) => s.trackUnsavedPhoto);
  const beginPhotoUpload = useRequestsStore((s) => s.beginPhotoUpload);
  const endPhotoUpload = useRequestsStore((s) => s.endPhotoUpload);

  function photoUploadError(file: File, cause: unknown): string {
    if (isPhotoCompressError(cause) && cause.code === 'decode-failed') {
      return t('requests:draft.photoDecodeFailed', { format: imageSourceFormat(file) });
    }
    if (isPhotoCompressError(cause) && cause.code === 'encode-failed') {
      return t('requests:draft.photoEncodeFailed');
    }
    return t('requests:draft.photoUploadFailed');
  }

  const [itemUploading, setItemUploading] = useState<Record<string, boolean>>({});
  const [itemUploadError, setItemUploadError] = useState<Record<string, string>>({});
  // WHY (#599): nested-item upload state keyed by composite
  //   `${boxClientId}/${boxItemClientId}` so DraftBoxesSection can
  //   surface per-row spinner + error without colliding with the
  //   ordinary items map above.
  const [boxItemUploading, setBoxItemUploading] = useState<Record<string, boolean>>({});
  const [boxItemUploadError, setBoxItemUploadError] = useState<Record<string, string>>({});
  // INVARIANT (#1451, D-B3): synchronous per-item in-flight lock for the
  //   multi-photo add path. `itemUploading` is async React state, so two
  //   rapid `onAddItemPhoto` calls for the same item (a double click
  //   before the disabled tile re-renders) could both pass the count
  //   check and start parallel PUTs. This Set is checked + mutated
  //   before any `await`, and cleared in the `finally`, so only one add
  //   per item is ever in flight.
  const inFlightItemsRef = useRef<Set<string>>(new Set());

  async function onUploadPhoto(itemId: string, file: File): Promise<void> {
    if (isUnsupportedSvgImage(file)) {
      setItemUploadError((m) => ({
        ...m,
        [itemId]: t('requests:draft.photoClipboard.unsupportedType'),
      }));
      return;
    }
    setItemUploading((m) => ({ ...m, [itemId]: true }));
    setItemUploadError((m) => {
      if (!(itemId in m)) return m;
      const next = { ...m };
      delete next[itemId];
      return next;
    });
    // INVARIANT (#521): increment the store-level upload counter
    // BEFORE the storage PUT. DraftDrawer.requestClose blocks while
    // the counter is non-zero so a close cannot race with an
    // in-flight upload — without this, a key resolved AFTER
    // closeDraft() lands in `unsavedPhotoKeys` of an already-wiped
    // scratchpad and never reaches the cleanup endpoints. The
    // matching decrement runs in the finally branch so an upload
    // error path still settles the counter.
    beginPhotoUpload();
    try {
      const blob = await compressImage(file);
      const presigned = await requestUploadUrl({
        contentType: 'image/jpeg',
        contentLength: blob.size,
        scope: 'request-item-photo',
      });
      const putRes = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'content-type': 'image/jpeg' },
      });
      if (!putRes.ok) throw new Error(`upload failed: ${putRes.status}`);
      // Pin the key as unsaved BEFORE writing it onto the item so a
      // close that races with the next setItemPhoto still includes
      // the fresh key in the cleanup batch.
      trackUnsavedPhoto(presigned.objectKey);
      setItemPhoto(itemId, presigned.objectKey);
    } catch (cause) {
      setItemUploadError((m) => ({ ...m, [itemId]: photoUploadError(file, cause) }));
    } finally {
      setItemUploading((m) => ({ ...m, [itemId]: false }));
      endPhotoUpload();
    }
  }

  // WHY (#1451): SIMPLE multi-photo add. Identical upload pipeline to
  //   `onUploadPhoto` (compress→presign→PUT→trackUnsavedPhoto, counter
  //   incremented BEFORE the PUT per INVARIANT #521) but writes via the
  //   append mutator `addDraftItemPhoto` instead of the replace path.
  //   Guard: read the live store and bail before starting the upload
  //   when the item already holds `REQUEST_ITEM_PHOTOS_MAX` photos so a
  //   stale render cannot push a key past the cap.
  async function onAddItemPhoto(itemId: string, file: File): Promise<{ objectKey: string } | null> {
    const item = useRequestsStore.getState().draft?.items.find((it) => it.id === itemId);
    if (item && item.photos.length >= REQUEST_ITEM_PHOTOS_MAX) return null;
    if (inFlightItemsRef.current.has(itemId)) return null;
    if (isUnsupportedSvgImage(file)) {
      setItemUploadError((m) => ({
        ...m,
        [itemId]: t('requests:draft.photoClipboard.unsupportedType'),
      }));
      return null;
    }
    inFlightItemsRef.current.add(itemId);
    setItemUploading((m) => ({ ...m, [itemId]: true }));
    setItemUploadError((m) => {
      if (!(itemId in m)) return m;
      const next = { ...m };
      delete next[itemId];
      return next;
    });
    beginPhotoUpload();
    try {
      const blob = await compressImage(file);
      const presigned = await requestUploadUrl({
        contentType: 'image/jpeg',
        contentLength: blob.size,
        scope: 'request-item-photo',
      });
      const putRes = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'content-type': 'image/jpeg' },
      });
      if (!putRes.ok) throw new Error(`upload failed: ${putRes.status}`);
      trackUnsavedPhoto(presigned.objectKey);
      addItemPhoto(itemId, presigned.objectKey);
      return { objectKey: presigned.objectKey };
    } catch (cause) {
      setItemUploadError((m) => ({ ...m, [itemId]: photoUploadError(file, cause) }));
      return null;
    } finally {
      inFlightItemsRef.current.delete(itemId);
      setItemUploading((m) => ({ ...m, [itemId]: false }));
      endPhotoUpload();
    }
  }

  // WHY (#599): same upload pipeline as `onUploadPhoto` but keyed by
  //   `(boxClientId, boxItemClientId)` and routes the resolved key
  //   through `setDraftBoxItemPhoto`. Storage scope is reused
  //   (`request-item-photo`) so cleanup endpoints on the backend
  //   accept the keys without a new scope namespace.
  async function onUploadBoxItemPhoto(
    boxClientId: string,
    boxItemClientId: string,
    file: File,
  ): Promise<void> {
    const key = `${boxClientId}/${boxItemClientId}`;
    if (isUnsupportedSvgImage(file)) {
      setBoxItemUploadError((m) => ({
        ...m,
        [key]: t('requests:draft.photoClipboard.unsupportedType'),
      }));
      return;
    }
    setBoxItemUploading((m) => ({ ...m, [key]: true }));
    setBoxItemUploadError((m) => {
      if (!(key in m)) return m;
      const next = { ...m };
      delete next[key];
      return next;
    });
    beginPhotoUpload();
    try {
      const blob = await compressImage(file);
      const presigned = await requestUploadUrl({
        contentType: 'image/jpeg',
        contentLength: blob.size,
        scope: 'request-item-photo',
      });
      const putRes = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'content-type': 'image/jpeg' },
      });
      if (!putRes.ok) throw new Error(`upload failed: ${putRes.status}`);
      trackUnsavedPhoto(presigned.objectKey);
      setBoxItemPhoto(boxClientId, boxItemClientId, presigned.objectKey);
    } catch (cause) {
      setBoxItemUploadError((m) => ({
        ...m,
        [key]: photoUploadError(file, cause),
      }));
    } finally {
      setBoxItemUploading((m) => ({ ...m, [key]: false }));
      endPhotoUpload();
    }
  }

  return {
    itemUploading,
    itemUploadError,
    boxItemUploading,
    boxItemUploadError,
    onUploadPhoto,
    onAddItemPhoto,
    onUploadBoxItemPhoto,
  };
}
